import {
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import {
  EMPTY_ROOM_STATE,
  type RoomHandle,
  type RoomMessage,
  type RoomRef,
  type RoomState,
  type UserEvent,
  type WebsocketAPI,
} from "simplydo/core/api/websocket";

export const useWebsocketEvent = <T = any,>(
  websocketApi: WebsocketAPI,
  event: UserEvent,
  handler: (payload: T) => void,
  enabled = true,
) => {
  const onEvent = useEffectEvent(handler);
  useEffect(() => {
    if (!enabled) return;
    return websocketApi.on<T>(event, (payload) => onEvent(payload));
  }, [websocketApi, event, enabled]);
};

/**
 * Joins the given room while mounted. Pass `null` to not join any room.
 * Returns the role assigned by the server and the visible members of the room,
 * send events to the room with `websocketApi.signal`.
 * Changing `rejoinKey` re-joins the room so the server re-evaluates the role.
 */
export const useWebsocketRoom = (
  websocketApi: WebsocketAPI,
  room: RoomRef | null,
  onMessage: (message: RoomMessage) => void,
  { rejoinKey }: { rejoinKey?: unknown } = {},
) => {
  const handle = useRef<RoomHandle | null>(null);
  const onRoomMessage = useEffectEvent(onMessage);
  const [state, setState] = useState<RoomState>(EMPTY_ROOM_STATE);
  const type = room?.type;
  const id = room?.id;

  useEffect(() => {
    if (!type || !id) return;
    const joined = websocketApi.joinRoom(
      { type, id },
      (message) => onRoomMessage(message),
      setState,
    );
    handle.current = joined;
    return () => {
      joined.leave();
      setState(EMPTY_ROOM_STATE);
      if (handle.current === joined) handle.current = null;
    };
  }, [websocketApi, type, id]);

  const lastRejoinKey = useRef(rejoinKey);
  useEffect(() => {
    if (Object.is(lastRejoinKey.current, rejoinKey)) return;
    lastRejoinKey.current = rejoinKey;
    handle.current?.rejoin();
  }, [rejoinKey]);

  return state;
};

export const useWebsocketStatus = (websocketApi: WebsocketAPI) =>
  useSyncExternalStore(
    (onChange) => websocketApi.onStatusChange(onChange),
    () => websocketApi.status,
  );

// Runs after the connection was re-established, to refetch anything that may have been missed
export const useWebsocketReconnect = (
  websocketApi: WebsocketAPI,
  handler: () => void,
) => {
  const onReconnect = useEffectEvent(handler);
  useEffect(
    () => websocketApi.onReconnect(() => onReconnect()),
    [websocketApi],
  );
};
